/**
 * D2/D3. Orthanc bakes an ABSOLUTE BulkDataURI from its own DicomWeb.Host
 * (.deploy/local/orthanc.json:86 -- "127.0.0.1", no port), so the URI it hands the viewer
 * resolves to port 80 and retrieveBulkData() fails. Measured live 2026-08-13 against the M0
 * RTSTRUCT: http://127.0.0.1/dicom-web/studies/…/instances/…/bulk/00111010.
 *
 * Correcting orthanc.json is NOT the repair, which is what M0 debt item 14 gets wrong
 * (m0-baseline/findings.md:1559-1566): "127.0.0.1:8042" is a URI that is correct and still
 * unusable, because it is cross-origin from localhost:3000 and bypasses the dev proxy that
 * exists precisely to avoid CORS. Re-rooting onto the PACS root the app already uses puts
 * the request back on the same proxy locally and the same host in a deployment.
 *
 * Returns `undefined` -- not the input -- when there is nothing to do, because the caller is
 * fixBulkDataURI.ts:26, `uriConfig.transform?.(BulkDataURI) || BulkDataURI`.
 */
export function rerootBulkDataUri(bulkDataUri: string, pacsRoot: string): string | undefined {
  if (typeof bulkDataUri !== 'string' || !/^https?:\/\//i.test(bulkDataUri)) {
    return undefined;
  }

  try {
    const { pathname, search } = new URL(bulkDataUri);
    return `${pacsRoot.replace(/\/+$/, '')}${pathname}${search}`;
  } catch {
    return undefined;
  }
}

interface BulkDataReference {
  BulkDataURI?: string;
  Value?: unknown;
  retrieveBulkData?: () => Promise<unknown>;
}

function toBuffer(value: unknown): ArrayBuffer | null {
  if (value instanceof ArrayBuffer) {
    return value;
  }

  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer;
  }

  // Orthanc answers the bulk endpoint as multipart/related even when the client asks for
  // singlepart (measured 2026-08-13), and retrieveBulkData.ts:23-27 already picks the first
  // non-empty part out of that. This handles the shape anyway, because value.Value is
  // whatever the resolver last stored and nothing guarantees which branch produced it.
  if (Array.isArray(value)) {
    for (const part of value) {
      const buffer = toBuffer(part);

      if (buffer?.byteLength) {
        return buffer;
      }
    }
  }

  return null;
}

/**
 * M0 measured the resolved bytes as 4a 47 52 00 -- "JGR\0". DICOM pads odd-length values
 * with a trailing NUL, so it is real and is trimmed rather than rendered.
 */
function decodeBulkDataText(value: unknown): string | null {
  const buffer = toBuffer(value);

  if (!buffer?.byteLength) {
    return null;
  }

  return new TextDecoder().decode(buffer).replace(/\0+$/, '').trim() || null;
}

/**
 * Over DICOMweb a private tag with VR `UN` arrives as a REFERENCE, not a value:
 * { BulkDataURI, vr }, with a resolver bound onto it at ingest
 * (DicomWebDataSource/index.ts:172-175). develop read the property directly
 * (useSeriesOrganReviewsOverlayStore.ts:82) and on 3.13 would render "[object Object]" into
 * `QA - {name}` -- the rendering defect parent :1030 predicted.
 *
 * `Value` is checked FIRST and that is not an optimisation. retrieveBulkData.ts:19 calls the
 * client UNCONDITIONALLY and only then writes value.Value at :28; the `!value.Value` guard
 * lives in addRetrieveBulkDataNaturalized (DicomWebDataSource/index.ts:172) and governs
 * BINDING at ingest, not calling. Without this check every call is a fresh network request,
 * which is the traffic §5.3 exists to remove.
 */
export async function readBulkDataText(value: unknown): Promise<string | null> {
  if (typeof value === 'string') {
    return value.replace(/\0+$/, '').trim() || null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const reference = value as BulkDataReference;

  if (reference.Value !== undefined) {
    return decodeBulkDataText(reference.Value);
  }

  if (typeof reference.retrieveBulkData !== 'function') {
    return null;
  }

  return decodeBulkDataText(await reference.retrieveBulkData());
}
